import React from 'react';
import { createPortal } from 'react-dom';
import { X, CreditCard, Loader2, Copy } from 'lucide-react';
import { Button } from '@/components/ui';
import { useCreateSubscription } from '@/services/mutations/company';
import { useGetPlans } from '@/services/queries/company';
import { toast } from 'react-toastify';
import { buildCheckoutRedirectUrl } from '@/utils/checkoutRedirectOrigin';

interface SubscribePlanModalProps {
	isOpen: boolean;
	onClose: () => void;
	companyId: string;
}

export const SubscribePlanModal = ({
	isOpen,
	onClose,
	companyId,
}: SubscribePlanModalProps) => {
	const { data: plans, isLoading: isLoadingPlans } = useGetPlans({
		active: true,
	});

	const [generatedUrlByPlanId, setGeneratedUrlByPlanId] = React.useState<{
		[planId: string]: string;
	}>({});
	const [generatingPlanId, setGeneratingPlanId] = React.useState<
		string | null
	>(null);

	const { mutateAsync: createSubscriptionAndGo, isPending: isSubscribing } =
		useCreateSubscription((result) => {
			if (result?.url) window.location.href = result.url;
		});

	const { mutateAsync: generateCheckoutUrl, isPending: isGeneratingUrl } =
		useCreateSubscription();

	const handleSubscribe = async (planId: string) => {
		const successUrl = buildCheckoutRedirectUrl(
			`/admin/empresas/${companyId}`,
		);
		const cancelUrl = buildCheckoutRedirectUrl(
			`/admin/empresas/${companyId}`,
		);

		await createSubscriptionAndGo({
			companyId,
			planId,
			successUrl,
			cancelUrl,
		});
	};

	const handleGenerateWithoutRedirect = async (planId: string) => {
		const successUrl = buildCheckoutRedirectUrl(
			`/admin/empresas/${companyId}`,
		);
		const cancelUrl = buildCheckoutRedirectUrl(
			`/admin/empresas/${companyId}`,
		);

		setGeneratingPlanId(planId);
		try {
			const result = await generateCheckoutUrl({
				companyId,
				planId,
				successUrl,
				cancelUrl,
			});

			if (result?.url) {
				setGeneratedUrlByPlanId((prev) => ({
					...prev,
					[planId]: result.url,
				}));

				// Quando o usuário pedir "Gerar sem ir", copia automaticamente no final.
				try {
					await navigator.clipboard.writeText(result.url);
					toast.success('URL copiada!');
				} catch {
					toast.error('Não foi possível copiar a URL.');
				}
			}
		} finally {
			setGeneratingPlanId(null);
		}
	};

	const handleCopyGeneratedUrl = async (planId: string) => {
		const url = generatedUrlByPlanId[planId];
		if (!url) return;
		try {
			await navigator.clipboard.writeText(url);
			toast.success('URL copiada!');
		} catch {
			toast.error('Não foi possível copiar a URL.');
		}
	};

	if (!isOpen) return null;

	const plansList = Array.isArray(plans) ? plans : [];

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200">
				<div className="flex shrink-0 items-center justify-between border-b border-stone-50 px-8 py-6">
					<h2 className="text-lg font-black text-stone-900">
						Assinar plano
					</h2>
					<button
						type="button"
						onClick={onClose}
						className="text-stone-400 transition-colors hover:text-stone-600"
					>
						<X size={20} />
					</button>
				</div>

				<div className="flex-1 overflow-y-auto p-8">
					{isLoadingPlans ? (
						<div className="flex items-center justify-center py-12">
							<Loader2 className="size-8 animate-spin text-stone-300" />
						</div>
					) : plansList.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-12 text-center">
							<div className="mb-3 rounded-full bg-stone-100 p-4">
								<CreditCard
									size={28}
									className="text-stone-400"
								/>
							</div>
							<p className="text-sm font-bold text-stone-600">
								Nenhum plano disponível
							</p>
							<p className="mt-1 text-xs text-stone-400">
								Cadastre planos empresariais na aba Planos de
								empresas.
							</p>
						</div>
					) : (
						<div className="space-y-2">
							{plansList.map((plan: Companies.Plan) => {
								const currency = plan.currency || 'BRL';
								const base = Number(plan.base_cost || 0);
								const maintenance = 0;
								const subsidy = Number(
									plan.subsidy_per_employee || 0,
								);
								const totalEstimated =
									base +
									maintenance +
									subsidy * plan.max_employees;

								const formatMoney = (value: number) =>
									new Intl.NumberFormat('pt-BR', {
										style: 'currency',
										currency,
									}).format(value);

								return (
									<div
										key={plan.id}
										className="group flex items-center justify-between rounded-2xl border border-stone-100 bg-stone-50/50 p-4 transition-all hover:border-stone-200 hover:bg-white"
									>
										<div className="flex flex-col gap-1">
											<span className="text-sm font-bold text-stone-900">
												{plan.name}
											</span>
											<p className="text-xs text-stone-500">
												{plan.max_employees}{' '}
												colaboradores
											</p>
											<div className="text-xs text-stone-500">
												<span className="font-semibold text-stone-600">
													Base:{' '}
												</span>
												{formatMoney(base)}{' '}
												<span className="text-stone-400">
													/mês
												</span>
											</div>
											<div className="text-xs text-stone-500">
												<span className="font-semibold text-stone-600">
													Base + Manutenção:{' '}
												</span>
												{formatMoney(
													base + maintenance,
												)}{' '}
												<span className="text-stone-400">
													/mês
												</span>
											</div>
											<div className="text-xs text-stone-500">
												<span className="font-semibold text-stone-600">
													Subsídio:{' '}
												</span>
												{formatMoney(subsidy)}{' '}
												<span className="text-stone-400">
													/func.
												</span>{' '}
												·{' '}
												{formatMoney(
													subsidy *
														plan.max_employees,
												)}{' '}
												<span className="text-stone-400">
													/mês
												</span>
											</div>
											<div className="text-xs text-stone-500">
												<span className="font-semibold text-stone-600">
													Custo total estimado:{' '}
												</span>
												{formatMoney(totalEstimated)}
												<span className="text-stone-400">
													{' '}
													/mês
												</span>
											</div>
										</div>
										<div className="flex flex-col items-end gap-2">
											{generatedUrlByPlanId[plan.id] ? (
												<Button
													text="Copiar"
													className="rounded-full border border-stone-200 bg-white px-4 py-2 text-[11px] font-bold text-stone-700 shadow-sm hover:bg-stone-50"
													startIcon={
														<Copy className="size-3.5 stroke-[3]" />
													}
													onClick={() =>
														handleCopyGeneratedUrl(
															plan.id,
														)
													}
												/>
											) : (
												<>
													<Button
														text={
															isSubscribing
																? 'Abrindo...'
																: 'Assinar e ir'
														}
														disabled={isSubscribing}
														className="rounded-full bg-black px-4 py-2 text-xs font-bold text-white shadow-md hover:bg-neutral-800 disabled:opacity-70"
														startIcon={
															isSubscribing ? (
																<Loader2 className="size-3.5 animate-spin" />
															) : (
																<CreditCard className="size-3.5 stroke-[3]" />
															)
														}
														onClick={() =>
															handleSubscribe(
																plan.id,
															)
														}
													/>

													<Button
														text={
															isGeneratingUrl &&
															generatingPlanId ===
																plan.id
																? 'Gerando...'
																: 'Gerar sem ir'
														}
														disabled={
															isGeneratingUrl
														}
														className="rounded-full border border-stone-200 bg-white px-4 py-2 text-xs font-bold text-stone-700 shadow-sm hover:bg-stone-50 disabled:opacity-70"
														startIcon={
															<Copy className="size-3.5 stroke-[3]" />
														}
														onClick={() =>
															handleGenerateWithoutRedirect(
																plan.id,
															)
														}
													/>
												</>
											)}
										</div>
									</div>
								);
							})}
						</div>
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
};
